
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Watch, Vue } from "vue-property-decorator";

// components
import Spinner from "../components/Spinner.vue";
import HazardListScreen from "../components/HazardListScreen.vue";
import HazardListPrint from "../components/HazardListPrint.vue";
import MapTile from "../components/MapTile.vue";

// utils
import ArcgisAuthenticate from "../utils/ArcgisAuthenticate";
import { pushCoords } from "../utils/Routing";
import Transform from "../utils/Transform";

// libaries
import axios from "axios";
import { Dictionary } from "vue-router/types/router";
import { cloneDeep, get, merge } from "lodash";

// instances of util/libary classes
const Axios = axios.create({ timeout: 30000 });
const AxiosBOM = axios.create({ timeout: 30000 });
const arcgisAuthenticate = new ArcgisAuthenticate();
const transform = new Transform();

@Component({
  components: {
    Spinner,
    HazardListScreen,
    HazardListPrint
  }
})
export default class HazardList extends Vue {
  // computed prop params
  get hazardListParams() {
    return {
      hazardSiteList: this.hazardSiteList,
      hazardRouteList: this.hazardRouteList,
      noti: this.noti,
      workOrder: this.workOrder,
      accuracy: this.accuracy,
      defaultLocationList: this.defaultLocationList,
      selectedLocation: this.selectedLocation,
      route: this.route,
      trackPath: this.trackPath,
      endCoords: this.endCoords,
      otherTracks: this.otherTracks,
      accessPoints: this.accessPoints,
      defibrillators: this.defibrillators,
      customersRetrieved: this.customersRetrieved
    };
  }
  // private attributes
  public hazardSiteList: object[] = [];
  public hazardRouteList: object[] = [];
  private accuracy: number | null = null;
  private queryParams: string | null = null;
  public error = "";
  private showStatuses: boolean[] = [];
  public onScreen = true;
  public printed = true;
  private customersRetrieved = false;
  private customerDataError = "";

  // URL parameters
  private origin?: string | null;
  private destination?: string | null;
  private assetOnly? = false;
  private noti?: string | null;
  private workOrder?: string | null;

  // variables for the map and default list
  private startCoords: number[] | null = null;
  private endCoords: number[] | null = null;
  private route = [];
  private trackPath = [];
  private otherTracks = [];
  private accessPoints = [];
  private defibrillators = [];

  // selected and default locations
  private defaultLocationList = cloneDeep(Vue.prototype.startingLocations);
  private selectedLocation = this.defaultLocationList[0];
  private currentLocation: Record<
    string,
    string | null | boolean | number[]
  > = {};

  $refs!: {
    screenList: HazardListScreen;
  };

  /*
    Builds a query string using start and end coords
    and authentication fields from local storage
    Used for the call to the back end
  */
  private buildQueryParams(
    startCoords: number[] | null,
    endCoords?: number[] | null
  ): string {
    const authCode = arcgisAuthenticate.getItem("auth_code");
    const redirectUri = arcgisAuthenticate.getItem("redirect_uri");
    const queryArray = [];
    if (startCoords) queryArray.push(`point=${startCoords.join(",")}`);
    if (endCoords) queryArray.push(`point=${endCoords.join(",")}`);
    if (authCode) queryArray.push(`code=${authCode}`);
    if (authCode && redirectUri) queryArray.push(`redirect_uri=${redirectUri}`);
    return queryArray.length == 0 ? "" : "?" + queryArray.join("&");
  }

  public mapLoaded() {
    this.printed = false;
  }

  mounted() {
    /*
      Adds "before" and "after" print listeners
    */
    const beforePrint = () => {
      this.onScreen = false;
      this.printed = true;
    };

    const afterPrint = () => {
      this.onScreen = true;
      this.printed = false;
      this.$nextTick(() => {
        const mapTile = this.$refs.screenList.$refs.screenMap as MapTile;
        mapTile.invalidateSize();
      });
    };

    window.onbeforeprint = beforePrint;
    window.onafterprint = afterPrint;

    /*
      Authentication
    */
    const authenticate = () => {
      // auth code returned from ArcGIS portal
      const authCode = this.$route.query.code;
      // if we have a code, remove the code from the URL
      if (authCode) {
        const query: Dictionary<string> = {};
        Object.keys(this.$route.query).forEach((key: string) => {
          if (key != "code") query[key] = this.$route.query[key] as string;
        });
        this.$router.replace({ query: query });
      }
      try {
        // authenticate if neeeded
        arcgisAuthenticate.authenticate(
          authCode as string,
          window.location.origin + this.$route.fullPath
        );
      } catch (ex) {
        this.error = ex as string;
      }
    };

    /*
      Getter and getter for start coords from local storage
    */
    const setStartCoords = (coords: number[]): void => {
      window.localStorage.setItem("startCoords", coords.join(","));
    };

    const getStartCoords = (): number[] | null => {
      if (!window.localStorage.getItem("startCoords")) return null;
      const coords = (window.localStorage.getItem("startCoords") as string)
        .split(",")
        .map((coord: string) => {
          return parseFloat(coord);
        });
      return coords.length == 2 ? coords : null;
    };

    /*
      Get a default location from the abbreviation
    */
    const getDefaultLocation = (
      abbr: string
    ): Record<string, string | number[] | boolean | null> => {
      return this.defaultLocationList.filter((el: Record<string, string>) => {
        return el.abbr == abbr;
      })[0];
    };

    /*
      Set up default locations
    */
    const setDefaultLocations = () => {
      const _addDefaultLocation = (
        location: Record<string, string | number[] | boolean | null>
      ) => {
        if (
          this.defaultLocationList.filter((loc: Record<string, string>) => {
            return loc.abbr == location.abbr;
          }).length == 0
        ) {
          this.defaultLocationList.unshift(location);
        }
      };
      // add "Asset Only" to default list
      _addDefaultLocation({
        label: "Asset Only",
        abbr: "asset",
        location: null
      });

      // add "Map"
      _addDefaultLocation({
        label: "Last used origin coordinates",
        location: getStartCoords(),
        abbr: "map",
        hidden: getStartCoords() ? false : true
      });

      // add "Current"
      _addDefaultLocation({
        label: "Current location",
        location: null,
        abbr: "here",
        disabled: true
      });
    };

    /*
      Extract query params from URL
    */
    const setVariablesFromQueryParams = (query: any) => {
      // get 'noti'/'destination' from URL
      this.noti = "noti" in query ? (query.noti as string) : null;
      this.workOrder =
        "work_order" in query ? (query.work_order as string) : null;
      this.destination =
        !this.noti && "destination" in query
          ? (query.destination as string)
          : null;
      if (this.destination)
        this.endCoords = this.destination.split(",").map((x: string) => {
          return +x;
        });

      // assetOnly in URL: set selected location to asset
      if ("assetOnly" in query) {
        this.assetOnly = true;
        this.selectedLocation = getDefaultLocation("asset");
      }

      // otherwise we look for origin (or location) in URL
      else if (query.origin || query.location) {
        this.origin = (query.origin || query.location) as string;
        // if origin is an abbreviation (e.g. 'origin=keswick')
        // we try to match it to one of default locations from the list
        // and set the start coords from that matched location
        this.selectedLocation = getDefaultLocation(this.origin);
        if (this.selectedLocation) {
          this.startCoords = this.selectedLocation.location;
        } else {
          // otherwise we try to convert them to comma separated
          // coords and put them in local storage if found
          this.startCoords = (this.origin as string).split(",").map(x => {
            return +x;
          });
          if (this.startCoords) {
            setStartCoords(this.startCoords);
            this.selectedLocation = getDefaultLocation("map");
            this.selectedLocation.hidden = false;
          }
        }
      }
    };

    /*
      Get geolocated coordinates and set location for "Current"
    */
    const getGeoLocation = () => {
      if (!navigator.geolocation) {
        const errMsg = "Geolocation is not supported by your browser";
        console.error(errMsg);
        if (!this.startCoords) {
          this.assetOnly = true;
          this.selectedLocation = getDefaultLocation("asset");
        }
        this.queryParams = this.buildQueryParams(
          this.startCoords,
          this.endCoords
        );
      } else {
        const _success = (position: any) => {
          const currentCoords = [
            position.coords.latitude,
            position.coords.longitude
          ];
          const currentLocation = getDefaultLocation("here");
          currentLocation.location = currentCoords;
          currentLocation.disabled = false;
          if (!this.startCoords && !this.assetOnly) {
            this.selectedLocation = currentLocation;
            this.startCoords = currentCoords;
          }
          this.queryParams = this.buildQueryParams(
            this.startCoords,
            this.endCoords
          );
        };
        const _failure = () => {
          const errMsg = "Unable to retrieve your location";
          console.error(errMsg);
          if (!this.startCoords) {
            this.assetOnly = true;
            this.selectedLocation = getDefaultLocation("asset");
          }
          this.queryParams = this.buildQueryParams(
            this.startCoords,
            this.endCoords
          );
        };
        navigator.geolocation.getCurrentPosition(
          _success.bind(this),
          _failure.bind(this),
          { timeout: 5000 }
        );
      }
    };

    /*
      Call functions above in order
    */
    authenticate();
    setDefaultLocations();
    setVariablesFromQueryParams(this.$route.query);
    getGeoLocation();
  }

  /*
    Creates a nested tree structure from the raw list
    from the back end. Hazards are grouped by hazard type
  */
  private createNestedTree(rawList: object[]): object[] {
    const rawListCopy = cloneDeep(rawList);
    const treeList: Record<string, any> = {};
    // create empty lists with hazard type as key
    rawListCopy.forEach((el: any) => {
      if (!(el.hazardType in treeList)) treeList[el.hazardType] = [];
      treeList[el.hazardType].push(el);
    });
    // special sorting required for CFS incidents
    Object.keys(treeList).forEach((k: string) => {
      if (k == "cfs incident") {
        treeList[k].sort((f1: any): number => {
          return f1.Status == "GOING" ? -1 : f1.Status == "CONTROLLED" ? 0 : 1;
        });
      }
    });
    // returns the nested tree
    return Object.keys(treeList).map((k: string) => {
      return {
        hazardType: k,
        displayName: treeList[k][0].displayName,
        count: treeList[k].length,
        displayCount: treeList[k].reduce(
          (a: number, b: any) => a + (b.count || 1),
          0
        ),
        hazards: treeList[k]
      };
    });
  }

  /*
    Returns a static object (dict) keyed by customer data type
    with fields to show or format keys coming from the back end
  */
  private getHazardFields(): Record<
    string,
    Record<string, string | Function>[]
  > {
    const rawHazardFields: Record<
      string,
      Record<string, string | Function>[]
    > = {
      "customer data": [
        { description: "Name", key: "customerName", default: "Not retrieved" },
        { description: "Address", key: "customerAddress" },
        { description: "Home Phone", key: "contactHome" },
        {
          description: "Mobile Phone",
          key: "contactMob",
          default: "Not retrieved"
        },
        { description: "Issue", key: "customerDesc" },
        { description: "Details", key: "customerComm" },
        { description: "Meter Location", key: "meterLocation" }
      ],
      "access track,surrounding track": [
        { description: "Type", key: "state" },
        { description: "Conditions", key: "conditions" },
        { description: "Notes", key: "notes" }
      ],
      "access point": [
        { description: "State", key: "state" },
        { description: "Type", key: "type" },
        { description: "Conditions", key: "conditions" },
        { description: "Contact Name", key: "contactName" },
        { description: "Phone Number", key: "phoneNumber" },
        { description: "Notes", key: "notes" }
      ],
      "fire ban district": [
        {
          description: "District",
          key: "cfsDistrict",
          formatter: transform.toTitleCase
        },
        {
          description: "Fire Danger Rating",
          key: "fireDangerRating",
          default: "Unavailable"
        }
      ],
      "cfs incident": [
        {
          description: "District",
          key: "incidentFBD",
          formatter: transform.toTitleCase
        },
        { description: "Type", key: "incidentType" },
        {
          description: "Status",
          key: "incidentStatus",
          formatter: transform.toTitleCase
        },
        {
          description: "Location",
          key: "incidentLocName",
          formatter: transform.toTitleCase
        },
        { description: "Date", key: "incidentDate" }
      ],
      "declared weed,significant roadside vegetation,contaminated site": [
        { description: "Description", key: "description" },
        { description: "Location", key: "location" }
      ],
      "weather warning": [{ description: "", key: "bomMessage" }],
      // eslint-disable-next-line prettier/prettier
      "ses_incident": [
        {
          description: "Description",
          key: "incidentTypeDescription",
          formatter: transform.toTitleCase
        },
        {
          description: "Status",
          key: "status",
          formatter: transform.toTitleCase
        },
        {
          description: "Location",
          key: "incidentLocation",
          formatter: transform.toTitleCase
        },
        {
          description: "Date",
          key: "lastUpdated",
          formatter: transform.toLocalDate
        }
      ],
      defibrillators: [
        {
          description: "Address",
          key: "address"
        },
        {
          description: "Details",
          key: "details",
          formatter: transform.removeNewLines
        },
        {
          description: "Distance",
          key: "coords"
        }
      ]
    };
    const hazardFields: Record<
      string,
      Record<string, string | Function>[]
    > = {};
    Object.keys(rawHazardFields).forEach((keys: string) => {
      const keyArray: string[] = keys.split(",");
      keyArray.forEach((key: string) => {
        hazardFields[key] = rawHazardFields[keys];
      });
    });
    return hazardFields;
  }

  /*
    From the hazard list, we get fields from the list
    and show/format those from the hazard field list
    and put those key/value pairs in a new field 'displayedFields'
  */
  private addDisplayFields(
    hazardList: object[],
    endCoords: number[] | null
  ): any {
    const hazardFields: Record<
      string,
      Record<string, string | Function>[]
    > = this.getHazardFields();
    const returnHazardList = cloneDeep(hazardList).map((el: any) => {
      const displayedFields: (string | Function)[][] = (
        hazardFields[el.hazardType] || []
      )
        .filter((field: Record<string, string | Function>) => {
          return (
            el[field.key as string] ||
            el[field.key as string] === 0 ||
            field.default
          );
        })
        // format the field if there is a formatter
        // and return string with description/value or just value
        .map((field: Record<string, string | Function>) => {
          let value: string;
          if (field.key == "coords" && endCoords && endCoords.length == 2) {
            value = transform.distance(
              [el[field.key as string][1], el[field.key as string][0]],
              this.endCoords as number[]
            );
          } else if (field.formatter) {
            value = (field.formatter as Function)(el[field.key as string]);
          } else {
            value = el[field.key as string];
          }
          return field.description
            ? [field.description, value || field.default]
            : [value || field.default];
        });
      // add an order number so that Vue will not complain about
      // duplicate keys
      el.displayedFields =
        displayedFields.length > 0
          ? (displayedFields as string[][]).map((f: string[], i: number) => {
              return { fields: f, order: i };
            })
          : [];
      return el;
    });
    return returnHazardList;
  }

  /*
    Determines a display status using various hazard fields
    The status is then used to determine background color
    and icon form and color.
    It also adds customer icons if hazard type is "customer data"
  */
  private addDisplayStatus(listToSort: object[]): object[] {
    const priorities = [
      "catastrophic",
      "extreme",
      "severe",
      "very-high",
      "high",
      "low-moderate",
      "no-fire-danger",
      "danger",
      "warning",
      "success",
      "info"
    ];

    const returnList = cloneDeep(listToSort).map((el: any) => {
      let displayStatus = "";
      el.displayStatus = "";
      const customerIconSet: Set<string> = new Set();
      el.hazards.forEach((hz: any) => {
        const customerSpecial = `${hz.customerDesc}`.toLowerCase();
        if (["fire ban district"].includes(el.hazardType)) {
          const map: Record<string, string> = {
            "No Fire Danger Rating": "no-fire-danger",
            Catastrophic: "catastrophic",
            Extreme: "extreme",
            Severe: "severe",
            "Very High": "very-high",
            High: "high",
            "Low-Moderate": "low-moderate"
          };
          displayStatus = map[hz.fireDangerRating];
        } else if (
          (["access point"].includes(el.hazardType) &&
            ["Owner Locked"].includes(hz.state)) ||
          ["Forbidden"].includes(hz.status) ||
          ["High Fire Risk"].includes(hz.bfraLevel) ||
          ["customer dogs"].includes(el.hazardType)
          // ["covid-19 cases", "customer dogs"].includes(el.hazardType)
        ) {
          displayStatus = "danger";
        } else if (
          (["access point"].includes(el.hazardType) &&
            (!hz.state || ["SAPN Locked"].includes(hz.state))) ||
          [
            "bio-security",
            "declared weed",
            "contaminated site",
            "significant roadside vegetation"
          ].includes(hz.hazardType)
        ) {
          displayStatus = "warning";
        } else if (
          (["access point"].includes(el.hazardType) &&
            ["Open"].includes(hz.state)) ||
          ["Preferred"].includes(hz.status) ||
          (["bushfire risk area"].includes(el.hazardType) && !hz.bfraLevel)
        ) {
          displayStatus = "success";
        } else if (
          ["Depricated"].includes(hz.status) || // depricated is misspelt - correct spelling is 'deprecated'
          ["fire ban district"].includes(el.hazardType)
        ) {
          displayStatus = "info";
        }
        // special icons for customer data
        const customerMap: Record<string, string> = {
          dog: "danger-dogs",
          customer: "danger-customer",
          life: "life-support",
          electric: "electric-hazard",
          general: "general-hazard",
          meterbox: "meterbox-hazard",
          "bio-hazard": "bio-hazard",
          weed: "bio-hazard"
        };
        if (customerSpecial) {
          Object.keys(customerMap).forEach((k: string) => {
            if (customerSpecial.includes(k)) {
              customerIconSet.add(customerMap[k]);
            }
          });
        }
        hz.displayStatus = displayStatus;
        if (
          !el.displayStatus ||
          priorities.indexOf(displayStatus) <
            priorities.indexOf(el.displayStatus)
        ) {
          el.displayStatus = displayStatus;
        }
      });
      el.customerIcons = [...customerIconSet];
      return el;
    });

    return returnList;
  }

  private combineIdenticalObjects(arr: any) {
    const result = [];
    const countMap = new Map();

    for (const obj of arr) {
      // Convert object to string for comparison
      const objStr = JSON.stringify(obj);

      if (countMap.has(objStr)) {
        // If the object is already in the map, increment the count
        const existingObj = countMap.get(objStr);
        existingObj.count++;
      } else {
        // If the object is not in the map, add it with a count of 1
        const newObj = { ...obj, count: 1 };
        countMap.set(objStr, newObj);
        result.push(newObj);
      }
    }
    const returnedResult = result.map((obj: any) => {
      if (obj.count && obj.count > 1) {
        obj.description = `${obj.description} x ${obj.count}`;
      }
      return obj;
    });
    return returnedResult;
  }

  /*
    Sorts hazard type groups by order list below
    so that 'customer data' will always be first, etc.
  */
  private sortList(routeList: object[]): object[] {
    const hazardOrder: string[] = [
      // "covid-19 cases",
      "fire ban district",
      "contaminated site",
      "significant roadside vegetation",
      "bio-security",
      "surrounding track",
      "access track",
      "access point",
      "customer data"
    ];
    const returnList = routeList.sort((a: any, b: any) => {
      return (
        hazardOrder.indexOf(b.hazardType) - hazardOrder.indexOf(a.hazardType)
      );
    });
    returnList.forEach((el: any, i: number) => {
      el.order = i;
    });
    return returnList;
  }

  /*
    From the raw hazard list from the back-end
    - Separate 'at asset' and 'en route'
    - Adds display fields for each hazard
    - Creates a nested tree, grouped by hazard type
    - Adds color status and icon details
  */
  private processData(inputHazardList: object[]): [object[], object[]] {
    console.log("inputHazardList", inputHazardList);

    // copy additional info fields at the top layer
    const rawHazardList = cloneDeep(inputHazardList).map((i: any) => {
      const hazard = i;
      if ("additionalInfo" in i) {
        Object.keys(i.additionalInfo).forEach((key: string) => {
          hazard[key] = i.additionalInfo[key];
        });
      }
      return hazard;
    });
    console.log("rawHazardList", rawHazardList);

    // separate 'en route' hazards and 'at asset' hazards
    // Access Points/Tracks are found at asset but displayed under 'En Route'
    let hazardRouteList = cloneDeep(rawHazardList).filter((i: any) => {
      return (
        !i.hazardContext ||
        i.hazardContext == "route" ||
        (i.hazardContext == "destination" &&
          ["access point", "access track", "surrounding track"].includes(
            i.hazardType
          ))
      );
    });

    // combine identical objects
    hazardRouteList = this.combineIdenticalObjects(hazardRouteList);

    // order items by description if displayName is "Declared Weed Zone"
    hazardRouteList = hazardRouteList.sort((a: any, b: any) => {
      if (
        a.displayName == "Declared Weed Zone" &&
        b.displayName == "Declared Weed Zone"
      ) {
        return a.description.localeCompare(b.description);
      }
      return 0;
    });

    // convert "raw" data from the backend and add fields that will be displayed
    hazardRouteList = this.addDisplayFields(hazardRouteList, this.endCoords);

    // create nested tree structure
    hazardRouteList = this.createNestedTree(hazardRouteList);

    // sort the list by hazard list
    hazardRouteList = this.sortList(hazardRouteList);

    // add background and icon details
    hazardRouteList = this.addDisplayStatus(hazardRouteList);

    // do same process but for site hazard list
    let hazardSiteList = cloneDeep(rawHazardList).filter((i: any) => {
      return (
        i.hazardContext == "destination" &&
        !["access point", "access track", "surrounding track"].includes(
          i.hazardType
        )
      );
    });
    hazardSiteList = this.addDisplayFields(hazardSiteList, this.endCoords);
    hazardSiteList = this.createNestedTree(hazardSiteList);
    hazardSiteList = this.sortList(hazardSiteList);
    hazardSiteList = this.addDisplayStatus(hazardSiteList);

    console.log("hazardRouteList", hazardRouteList);
    console.log("hazardSiteList", hazardSiteList);

    return [hazardRouteList, hazardSiteList];
  }

  /*
    If the origin location in the dropdown is changed, update
    origin and reload page with new origin
  */
  public updateLocation(location: string): void {
    if (location == this.selectedLocation) return;
    // determine origin or asset only
    this.assetOnly = false;
    if (location == "map") {
      const newLocation = this.defaultLocationList.filter((l: any) => {
        return l.abbr == "map";
      })[0];
      this.origin = (newLocation.location as number[])
        .map((x: number) => {
          return x.toString();
        })
        .join(",");
    } else if (location == "asset") {
      this.assetOnly = true;
      this.origin = null;
    } else if (location == "here") {
      this.origin = null;
    } else {
      this.origin = location as string;
    }
    // determine destination type
    this.destination = this.noti
      ? null
      : `${(this.endCoords as number[])[0]},${(this.endCoords as number[])[1]}`;
    pushCoords(this.destination, this.origin, this.assetOnly, this.noti);
    this.$router.go(0);
  }

  private handleError(err: any): string {
    console.log(err);
    let errMsg = "";
    const status = get(err, "response.status");
    const message = get(err, "message") || "";
    const data = get(err, "response.data");
    if (status && [498, 499].includes(status)) {
      try {
        arcgisAuthenticate.reauthenticate();
      } catch (ex) {
        errMsg =
          (ex as string) ||
          "Failed getting access tokens for the ArcGIS services";
      }
    } else if (message.indexOf("timeout") != -1) {
      errMsg = "Timeout getting hazard list from WorkMate back-end";
    } else if (typeof data == "string" && data.indexOf("ECONNREFUSED")) {
      errMsg = "WorkMate back-end is currently not responding or not available";
    } else if (typeof data == "object" && "error" in data) {
      errMsg = data.error;
    } else {
      errMsg = message;
    }
    return errMsg;
  }

  /*
    Tries to get sensitive customer details (name and phone numbers)
    from the back-end
  */
  public retrieveCustomers(done: CallableFunction): void {
    // retrieve sensitive customer data
    if (this.queryParams) {
      const url = `/api/customer-data${this.queryParams}`;
      console.log("url", url);
      Axios.get(url)
        .then(
          resp => {
            arcgisAuthenticate.resetRetryCount();
            if (!resp.data) {
              const errMsg = "No customer data returned for this destination";
              console.error(errMsg);
              this.error = errMsg;
            } else {
              const customerDataRaw = resp.data.hazards;
              customerDataRaw.forEach((hazard: any) => {
                if (hazard.additionalInfo) {
                  hazard.additionalInfo.customerName =
                    hazard.additionalInfo.customerName || "Not recorded";
                  hazard.additionalInfo.contactMob =
                    hazard.additionalInfo.contactMob || "Not recorded";
                }
              });
              const response: any = this.processData(customerDataRaw);
              const customerDataList: any = response[1];
              merge(this.hazardSiteList, customerDataList);
              console.log("this.hazardSiteList", this.hazardSiteList);
              this.customersRetrieved = true;
            }
          },
          err => {
            console.error(err);
            const customerDataError = this.handleError(err);
            done(customerDataError);
          }
        )
        .finally(() => {
          done();
        });
    }
  }

  @Watch("queryParams", {})
  onQueryParamsChanged(queryParams: string) {
    if (queryParams != "null") {
      const url = this.noti
        ? `/api/noti/${this.noti}/route-hazards${queryParams}`
        : this.workOrder
        ? `/api/work_order/${this.workOrder}/route-hazards${queryParams}`
        : `/api/route-hazards${queryParams}`;
      console.log(url);
      Axios.get(url).then(
        resp => {
          arcgisAuthenticate.resetRetryCount();
          if (!resp.data) {
            const errMsg = "No hazards returned for this route or destination";
            console.error(errMsg);
            this.error = errMsg;
          } else {
            /*
              Get road and track paths to show on the map
            */
            if ((get(resp, "data.route.road_path") || []).length) {
              this.route = resp.data.route.road_path.map(
                (el: [number, number][]) => {
                  return [el[1], el[0]]; // swaping long, lat to lat, long
                }
              );
            }
            if (!this.endCoords && get(resp, "data.route.asset")) {
              const asset = resp.data.route.asset;
              this.endCoords = [asset[1] as number, asset[0] as number];
              // rebuild query params for endCoord (will be used for customer info endpoint)
              this.queryParams = this.buildQueryParams(
                this.startCoords,
                this.endCoords
              );
            }
            if (get(resp, "data.route.track_path")) {
              this.trackPath = resp.data.route.track_path.map(
                (el: [number, number][]) => {
                  return [el[1], el[0]];
                }
              );
            }

            // if we have surrounding tracks that may or may not be in the hazard list
            // but we want to show these tracks on the map, regardless
            this.otherTracks = resp.data.hazards
              .filter((hz: any) => {
                return (
                  hz.hazardType == "surrounding track" &&
                  get(hz, "geometry.paths.0")
                );
              })
              .map((hz: any) => {
                // swap long/lat to lat/long
                return hz.geometry.paths[0].map((el: [number, number][]) => {
                  return [el[1], el[0]];
                });
              });
            // filter access points and put points on prop to send to map
            this.accessPoints = resp.data.hazards
              .filter((hz: any) => {
                return (
                  hz.hazardType == "access point" &&
                  get(hz, "geometry.x") &&
                  get(hz, "geometry.y")
                );
              })
              .map((hz: any) => {
                // swap long/lat to lat/long
                return [hz.geometry.y, hz.geometry.x];
              });
            // filters out the surrounding track that is same as the access track
            resp.data.hazards = resp.data.hazards.filter((hz: any) => {
              if (hz.hazardType != "surrounding track") {
                return true;
              } else {
                const accessTrack = resp.data.hazards.filter((hz: any) => {
                  return hz.hazardType == "access track";
                })[0];
                const trackId = accessTrack
                  ? accessTrack.additionalInfo.objectId
                  : -1;
                return hz.additionalInfo.objectId != trackId;
              }
            });
            // filter defibrillators and put points on prop to send to map
            this.defibrillators = resp.data.hazards
              .filter((hz: any) => {
                return (
                  hz.hazardType == "defibrillators" &&
                  get(hz, "geometry.x") &&
                  get(hz, "geometry.y")
                );
              })
              .map((hz: any) => {
                // swap long/lat to lat/long
                return [hz.geometry.y, hz.geometry.x];
              });
            const offsets: number[] | null = resp.data.route.offsets;
            if (offsets) {
              this.accuracy = Math.round(offsets[offsets.length - 1]);
            }
            [this.hazardRouteList, this.hazardSiteList] = this.processData(
              resp.data.hazards
            );
            /*
              Get BOM Hazards
            */
            const path = resp.data.route.path;
            const bomPlaceholder: Record<string, any> = {
              displayName: "Loading BOM Alerts...",
              hazardType: "bom placeholder",
              customerIcons: [],
              hazardClasses: [{ count: 0, hazardClass: "", hazards: [] }]
            };
            if (path.length == 1) {
              this.hazardSiteList.push(bomPlaceholder);
            } else {
              this.hazardRouteList.push(bomPlaceholder);
            }
            AxiosBOM.post("/api/bom-hazards", { route: resp.data.route }).then(
              resp => {
                const searchSuburbs: Set<string> = new Set(resp.data.suburbs);
                console.log("resp.data.hazards", resp.data.hazards);
                const bomHazards: Record<
                  string,
                  string | string[]
                >[] = resp.data.hazards.map((el: Record<string, any>) => {
                  const warningSuburbs: Set<string> = new Set(el.suburbs);
                  const matchedSuburbs: Set<string> = new Set(
                    [...searchSuburbs].filter(x => warningSuburbs.has(x))
                  );
                  el.bomLocation = [...matchedSuburbs].join(", ");
                  el.bomMessage =
                    el.bomMessage
                      .replace(/<p class="p-id">IS?D.*<\/p>/, "")
                      .replace(
                        '<p class="source">Australian Government Bureau of Meteorology<br/>South Australia</p>',
                        ""
                      )
                      .replace(/<h\d+([^>]*)>/g, "<b$1>")
                      .replace(/<\/h\d+>/g, "</b>") +
                    '<div class="h6">Source: Australian Government Bureau of Meteorology, South Australia</div>';
                  return el;
                });
                let convertedList: any[] = [];
                if (bomHazards.length == 0) {
                  convertedList = [
                    {
                      displayName: "No Current Weather Alerts",
                      hazardType: "bom no alerts",
                      hazardClass: "",
                      hazards: [],
                      order: 100
                    }
                  ];
                } else {
                  console.log("bomHazards", bomHazards);
                  convertedList = this.addDisplayFields(bomHazards, null);
                  convertedList = this.createNestedTree(convertedList);
                  convertedList = this.addDisplayStatus(convertedList);
                  convertedList.forEach((el: any, i: number) => {
                    el.order = 100 + i;
                  });
                }
                if (path.length == 1) {
                  this.hazardSiteList.pop();
                  this.hazardSiteList.push(...convertedList);
                } else {
                  this.hazardRouteList.pop();
                  this.hazardRouteList.push(...convertedList);
                }
              },
              err => {
                console.error(err);
                const rwaError: Record<string, any> = {
                  displayName: "Unable to retrieve BOM alerts",
                  hazardType: "bom error",
                  hazardClasses: [{ count: 0, hazardClass: "", hazards: [] }]
                };
                if (path.length == 1) {
                  this.hazardSiteList.pop();
                  this.hazardSiteList.push(rwaError);
                } else {
                  this.hazardRouteList.pop();
                  this.hazardRouteList.push(rwaError);
                }
              }
            );
          }
        },
        err => {
          this.error = this.handleError(err);
        }
      );
    }
  }
}
